import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  Lock,
  Users,
  Check,
  Link2,
  ChevronDown,
  Globe,
} from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { MemberSearchInput } from './MemberSearchInput'
import { GuestList } from './GuestList'
import type { GuestEntry } from './GuestList'
import type { OrgMemberOption } from './MemberSearchInput'

export type ShareAccessMode = 'private' | 'organization' | 'shared'

export interface ShareAccessOption {
  value: ShareAccessMode
  label: string
  description: string
  icon: typeof Lock
}

interface GeneralAccessSelectProps {
  value: ShareAccessMode
  options: Array<ShareAccessOption>
  onChange: (mode: ShareAccessMode) => void
  disabled?: boolean
}

function iconStyle(mode: ShareAccessMode) {
  if (mode === 'private') return { bg: 'bg-muted', fg: 'text-muted-foreground' }
  if (mode === 'organization') return { bg: 'bg-primary/10', fg: 'text-primary' }
  return { bg: 'bg-emerald-500/10', fg: 'text-emerald-600' }
}

export function GeneralAccessSelect(props: GeneralAccessSelectProps) {
  const { value, options, onChange, disabled } = props
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  function toggleOpen() {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 6, left: rect.left, width: rect.width })
    }
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const current = options.find((o) => o.value === value) || options[0]
  const CurrentIcon = current.icon
  const currentStyle = iconStyle(value)

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        data-testid="general-access-select"
        disabled={disabled}
        onClick={toggleOpen}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left',
          'transition-all hover:bg-accent/50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled && 'opacity-50 pointer-events-none',
          isOpen && 'bg-accent/50',
        )}
      >
        <span className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          currentStyle.bg,
        )}>
          <CurrentIcon className={cn('h-4 w-4', currentStyle.fg)} strokeWidth={1.5} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground leading-tight">{current.label}</span>
          <span className="text-xs text-muted-foreground leading-tight">{current.description}</span>
        </div>
        <ChevronDown className={cn(
          'h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform duration-200',
          isOpen && 'rotate-180',
        )} strokeWidth={2} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          data-testid="general-access-dropdown"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
          className={cn(
            'z-[100] overflow-hidden rounded-xl border border-border',
            'bg-white dark:bg-neutral-900',
            'shadow-lg shadow-black/10 dark:shadow-black/30',
            'animate-in fade-in slide-in-from-top-1 duration-150',
          )}
        >
          {options.map((option) => {
            const Icon = option.icon
            const isSelected = option.value === value
            const style = iconStyle(option.value)
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`access-option-${option.value}`}
                onClick={() => {
                  onChange(option.value)
                  setIsOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
                  'border-b border-border last:border-b-0',
                  isSelected
                    ? 'bg-neutral-50 dark:bg-neutral-800'
                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                )}
              >
                <span className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  style.bg,
                )}>
                  <Icon className={cn('h-4 w-4', style.fg)} strokeWidth={1.5} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground leading-tight">{option.label}</span>
                  <span className="text-xs text-muted-foreground leading-tight">{option.description}</span>
                </div>
                {isSelected && (
                  <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
                )}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </div>
  )
}

interface OrgVisibilityToggleProps {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}

export function OrgVisibilityToggle(props: OrgVisibilityToggleProps) {
  const { label, description, checked, disabled, onCheckedChange } = props

  return (
    <div
      data-testid="org-visibility-toggle-section"
      className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2.5"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground leading-tight">
          {label}
        </span>
        <span className="text-xs text-muted-foreground leading-tight">
          {description}
        </span>
      </div>
      <Switch
        data-testid="org-visibility-toggle"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}

interface GuestChatAccessSectionProps {
  label: string
  description: string
  enabled: boolean
  cap: number
  disabled?: boolean
  onToggle: () => void
  onCapChange: (cap: number) => void
}

export function GuestChatAccessSection(props: GuestChatAccessSectionProps) {
  const { label, description, enabled, cap, disabled, onToggle, onCapChange } = props
  const { t } = useTranslation('app')
  // Local draft keeps a string so the user can clear the field mid-edit
  // without immediately committing 0 (which means unlimited) to the server.
  const [draftCap, setDraftCap] = useState<string>(String(cap))

  useEffect(() => {
    setDraftCap(String(cap))
  }, [cap])

  function handleCapChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDraftCap(e.target.value)
  }

  function commitCap() {
    const parsed = parseInt(draftCap, 10)
    if (!isNaN(parsed) && parsed > 0) {
      onCapChange(parsed)
    } else {
      // Reset draft to last valid value on bad input
      setDraftCap(String(cap))
    }
  }

  return (
    <div
      data-testid="guest-chat-section"
      className="flex flex-col gap-2.5 rounded-lg bg-muted/40 px-3 py-2.5"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground leading-tight">
            {label}
          </span>
          <span className="text-xs text-muted-foreground leading-tight">
            {description}
          </span>
        </div>
        <Switch
          data-testid="guest-chat-toggle"
          checked={enabled}
          onCheckedChange={onToggle}
          disabled={disabled}
        />
      </div>
      {enabled && (
        <div className="flex items-center justify-between border-t border-border/50 pt-2">
          {cap === 0 ? (
            <>
              <span className="text-xs text-muted-foreground">{t('sharing.unlimitedMessages')}</span>
              <button
                type="button"
                data-testid="guest-chat-set-cap"
                onClick={() => onCapChange(25)}
                className="text-xs text-primary hover:underline"
              >
                {t('sharing.setLimit')}
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t('sharing.limitLabel')}</span>
                <input
                  type="number"
                  data-testid="guest-chat-cap-input"
                  min={1}
                  max={100}
                  value={draftCap}
                  onChange={handleCapChange}
                  onBlur={commitCap}
                  className={cn(
                    'h-6 w-14 rounded border border-border bg-background px-1.5 text-xs',
                    'text-foreground text-center',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                />
                <span className="text-xs text-muted-foreground">{t('sharing.messagesPerGuest')}</span>
              </div>
              <button
                type="button"
                data-testid="guest-chat-remove-cap"
                onClick={() => onCapChange(0)}
                className="text-xs text-primary hover:underline"
              >
                {t('sharing.removeLimit')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

interface PeopleWithAccessSectionProps {
  members: Array<OrgMemberOption>
  excludeUserIds: Array<string>
  guests: Array<GuestEntry>
  orgMemberEmails?: Set<string>
  disabled?: boolean
  onAddGuest: (email: string) => void
  onRemoveGuest: (id: string) => void
}

export function PeopleWithAccessSection(props: PeopleWithAccessSectionProps) {
  const {
    members,
    excludeUserIds,
    guests,
    orgMemberEmails,
    disabled,
    onAddGuest,
    onRemoveGuest,
  } = props
  const { t } = useTranslation('app')

  return (
    <div className={cn(
      'flex flex-col gap-3 px-6 py-5 transition-opacity',
      disabled && 'pointer-events-none opacity-40',
    )}>
      <span className="text-xs font-medium text-muted-foreground">
        People with access
      </span>
      <MemberSearchInput
        members={members}
        excludeUserIds={excludeUserIds}
        onSelect={(member) => onAddGuest(member.email)}
        allowExternalEmail
        onAddExternalEmail={onAddGuest}
        placeholder={t('sharing.addByEmail')}
      />
      {guests.length > 0 && (
        <div className="flex flex-col gap-1 mt-1">
          <GuestList
            guests={guests}
            orgMemberEmails={orgMemberEmails}
            onRemove={onRemoveGuest}
          />
        </div>
      )}
    </div>
  )
}

interface ShareModalFooterProps {
  shareUrl: string | null
  saving?: boolean
  copied: boolean
  onCopy: () => void
  onDone: () => void
}

export function ShareModalFooter(props: ShareModalFooterProps) {
  const { shareUrl, saving, copied, onCopy, onDone } = props
  const { t } = useTranslation('app')

  return (
    <div className="flex shrink-0 items-center justify-between border-t border-border px-6 py-4">
      <button
        type="button"
        data-testid="share-copy-link"
        onClick={onCopy}
        disabled={!shareUrl || saving}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5',
          'text-sm font-medium transition-all duration-150',
          !shareUrl || saving
            ? 'text-muted-foreground cursor-not-allowed opacity-50'
            : copied
              ? 'text-primary'
              : 'text-foreground hover:text-primary active:scale-[0.98]',
        )}
      >
        {copied
          ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          : <Link2 className="h-3.5 w-3.5" strokeWidth={2} />}
        {copied ? t('copied', { ns: 'common' }) : t('copyLink', { ns: 'common' })}
      </button>
      <button
        type="button"
        data-testid="share-modal-done"
        onClick={onDone}
        className={cn(
          'inline-flex items-center px-4 py-2 rounded-md',
          'bg-[hsl(var(--share-action))] text-[hsl(var(--share-action-foreground))]',
          'text-sm font-medium hover:brightness-110 active:scale-[0.98] transition-all',
        )}
      >
        {t('done', { ns: 'common' })}
      </button>
    </div>
  )
}

export const DEFAULT_ACCESS_ICONS = {
  private: Lock,
  organization: Users,
  shared: Globe,
}
